import React, { useState, useEffect, useRef } from 'react';
import { portfolioData } from '../data/portfolioData';
import '../styles/Portfolio.css';

const Portfolio = ({ onItemClick }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('animations'); // 'animations', 'rigging', 'storyboards', or 'characterdesigns'
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const sectionsRef = useRef(null);

  useEffect(() => {
    const container = sectionsRef.current;
    if (!container) return;
    const items = Array.from(container.querySelectorAll('.portfolio-item'));
    items.forEach((el) => el.classList.remove('reveal-visible'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    items.forEach((el, i) => {
      el.style.transitionDelay = `${Math.min(i, 6) * 90}ms`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [activeSection]);

  return (
    <section id="portfolio" className="portfolio">
      <div className="portfolio-container">
        <h2 className="section-title">Portfolio</h2>
        
        <div className="portfolio-nav-buttons">
          <button 
            className={`portfolio-nav-btn ${activeSection === 'animations' ? 'active' : ''}`}
            onClick={() => setActiveSection('animations')}
          >
            Animations
          </button>
          <button 
            className={`portfolio-nav-btn ${activeSection === 'rigging' ? 'active' : ''}`}
            onClick={() => setActiveSection('rigging')}
          >
            Rigging
          </button>
          <button 
            className={`portfolio-nav-btn ${activeSection === 'storyboards' ? 'active' : ''}`}
            onClick={() => setActiveSection('storyboards')}
          >
            Storyboards
          </button>
          <button 
            className={`portfolio-nav-btn ${activeSection === 'characterdesigns' ? 'active' : ''}`}
            onClick={() => setActiveSection('characterdesigns')}
          >
            Character Designs
          </button>
          <button 
            className={`portfolio-nav-btn ${activeSection === 'propdesigns' ? 'active' : ''}`}
            onClick={() => setActiveSection('propdesigns')}
            data-testid="portfolio-tab-propdesigns"
          >
            Prop Designs
          </button>
        </div>

        <div className="portfolio-sections" ref={sectionsRef}>
          {activeSection === 'animations' && (
            <>
              <div className="section-header">
                <h3 className="subsection-title">Animations</h3>
              </div>
              <div className="portfolio-grid">
                {portfolioData.filter(item => item.type === 'video' && item.category === 'animations').map((item) => (
                  <div 
                    key={item.id} 
                    className="portfolio-item" 
                    onClick={() => onItemClick(item)}
                    onMouseEnter={() => setHoveredVideo(item.id)}
                    onMouseLeave={() => setHoveredVideo(null)}
                  >
                    <div className="item-image-wrapper">
                      {hoveredVideo !== item.id ? (
                        <img 
                          src={item.thumbnail} 
                          alt={item.title} 
                          className="item-image" 
                        />
                      ) : item.videoFile ? (
                        <video
                          src={item.videoFile}
                          poster={item.thumbnail}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="item-video-iframe"
                        />
                      ) : (
                        <iframe
                          src={item.videoPreview}
                          title={item.title}
                          className="item-video-iframe"
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                        />
                      )}
                      <div className="item-overlay">
                        <span className="play-icon">▶</span>
                      </div>
                    </div>
                    <div className="item-info">
                      <h4 className="item-title">{item.title}</h4>
                      <p className="item-date">{item.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeSection === 'rigging' && (
            <>
              <div className="section-header">
                <h3 className="subsection-title">Rigging</h3>
              </div>
              <div className="portfolio-grid">
                {portfolioData.filter(item => item.type === 'video' && item.category === 'rigging').map((item) => (
                  <div 
                    key={item.id} 
                    className="portfolio-item" 
                    onClick={() => onItemClick(item)}
                    onMouseEnter={() => setHoveredVideo(item.id)}
                    onMouseLeave={() => setHoveredVideo(null)}
                  >
                    <div className="item-image-wrapper">
                      {hoveredVideo !== item.id ? (
                        <img 
                          src={item.thumbnail} 
                          alt={item.title} 
                          className="item-image" 
                        />
                      ) : item.videoFile ? (
                        <video
                          src={item.videoFile}
                          poster={item.thumbnail}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="item-video-iframe"
                        />
                      ) : (
                        <iframe
                          src={item.videoPreview}
                          title={item.title}
                          className="item-video-iframe"
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                        />
                      )}
                      <div className="item-overlay">
                        <span className="play-icon">▶</span>
                      </div>
                    </div>
                    <div className="item-info">
                      <h4 className="item-title">{item.title}</h4>
                      <p className="item-date">{item.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeSection === 'storyboards' && (
            <>
              <div className="section-header">
                <h3 className="subsection-title">Storyboards</h3>
              </div>
              <div className="portfolio-grid">
                {portfolioData.filter(item => item.category === 'storyboards').map((item) => (
                  <div key={item.id} className="portfolio-item" onClick={() => onItemClick(item)} data-testid={`portfolio-item-${item.id}`}>
                    <div className="item-image-wrapper">
                      <img src={item.thumbnail} alt={item.title} className="item-image" />
                      <div className="item-overlay">
                        <span className="view-icon">{item.type === 'pdf' ? '📄' : item.type === 'video' ? '▶' : '+'}</span>
                      </div>
                    </div>
                    <div className="item-info">
                      <h4 className="item-title">{item.title}</h4>
                      <p className="item-date">{item.date}</p>
                      {item.type === 'pdf' && <span className="item-category">PDF Document</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeSection === 'characterdesigns' && (
            <>
              <div className="section-header">
                <h3 className="subsection-title">Character Designs</h3>
              </div>
              <div className="portfolio-grid">
                {portfolioData.filter(item => item.category === 'characterdesigns').map((item) => (
                  <div key={item.id} className="portfolio-item" onClick={() => onItemClick(item)}>
                    <div className="item-image-wrapper">
                      <img src={item.thumbnail} alt={item.title} className="item-image" />
                      <div className="item-overlay">
                        <span className="view-icon">{item.type === 'pdf' ? '📄' : '+'}</span>
                      </div>
                    </div>
                    <div className="item-info">
                      <h4 className="item-title">{item.title}</h4>
                      <p className="item-date">{item.date}</p>
                      {item.type === 'pdf' && <span className="item-category">PDF Document</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeSection === 'propdesigns' && (
            <>
              <div className="section-header">
                <h3 className="subsection-title">Prop Designs</h3>
              </div>
              <div className="portfolio-grid" data-testid="propdesigns-grid">
                {portfolioData.filter(item => item.category === 'propdesigns').map((item) => (
                  <div key={item.id} className="portfolio-item" onClick={() => onItemClick(item)} data-testid={`portfolio-item-${item.id}`}>
                    <div className="item-image-wrapper">
                      <img src={item.thumbnail} alt={item.title} className="item-image" />
                      <div className="item-overlay">
                        <span className="view-icon">+</span>
                      </div>
                    </div>
                    <div className="item-info">
                      <h4 className="item-title">{item.title}</h4>
                      <p className="item-date">{item.date}</p>
                      <span className="item-category">{item.categoryLabel}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
